import React, { useContext, useMemo } from 'react';
import { createMemoStyles, safeRest, defaultBaseline, defaultFonts, withNested, withSpacing, clsx } from '@wds/utils';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

var GridContext = React.createContext({
    noGutters: false,
});

var useStyles = createMemoStyles(function (theme) {
    var _a;
    var spacing = theme.spacing, breakpoints = theme.breakpoints;
    var grid = {
        gutter: {
            xs: spacing[4],
            sm: spacing[5],
            md: spacing[5],
            lg: spacing[5],
            xl: spacing[5],
        },
        columns: 12,
    };
    var width = function (sizeValue) {
        return sizeValue ? "".concat(100 * (sizeValue / grid.columns), "%") : '0%';
    };
    return {
        wrenchGridItem: (_a = {
                boxSizing: 'border-box',
                flexGrow: 1,
                flexBasis: 0,
                maxWidth: '100%'
            },
            _a["@media (min-width: ".concat(breakpoints.xs, "px)")] = {
                flex: function (_a) {
                    var xs = _a.xs;
                    return xs && "0 0 ".concat(width(xs));
                },
                maxWidth: function (_a) {
                    var xs = _a.xs;
                    return xs && width(xs);
                },
                order: function (_a) {
                    var xsOrder = _a.xsOrder;
                    return xsOrder;
                },
                paddingLeft: function (_a) {
                    var noGutters = _a.noGutters;
                    return (noGutters ? 0 : grid.gutter.xs / 2);
                },
                paddingRight: function (_a) {
                    var noGutters = _a.noGutters;
                    return (noGutters ? 0 : grid.gutter.xs / 2);
                },
            },
            _a["@media (min-width: ".concat(breakpoints.sm, "px)")] = {
                flex: function (_a) {
                    var sm = _a.sm;
                    return sm && "0 0 ".concat(width(sm));
                },
                maxWidth: function (_a) {
                    var sm = _a.sm;
                    return sm && width(sm);
                },
                order: function (_a) {
                    var smOrder = _a.smOrder;
                    return smOrder;
                },
                paddingLeft: function (_a) {
                    var noGutters = _a.noGutters;
                    return (noGutters ? 0 : grid.gutter.sm / 2);
                },
                paddingRight: function (_a) {
                    var noGutters = _a.noGutters;
                    return (noGutters ? 0 : grid.gutter.sm / 2);
                },
            },
            _a["@media (min-width: ".concat(breakpoints.md, "px)")] = {
                flex: function (_a) {
                    var md = _a.md;
                    return md && "0 0 ".concat(width(md));
                },
                maxWidth: function (_a) {
                    var md = _a.md;
                    return md && width(md);
                },
                order: function (_a) {
                    var mdOrder = _a.mdOrder;
                    return mdOrder;
                },
                paddingLeft: function (_a) {
                    var noGutters = _a.noGutters;
                    return (noGutters ? 0 : grid.gutter.md / 2);
                },
                paddingRight: function (_a) {
                    var noGutters = _a.noGutters;
                    return (noGutters ? 0 : grid.gutter.md / 2);
                },
            },
            _a["@media (min-width: ".concat(breakpoints.lg, "px)")] = {
                flex: function (_a) {
                    var lg = _a.lg;
                    return lg && "0 0 ".concat(width(lg));
                },
                maxWidth: function (_a) {
                    var lg = _a.lg;
                    return lg && width(lg);
                },
                order: function (_a) {
                    var lgOrder = _a.lgOrder;
                    return lgOrder;
                },
                paddingLeft: function (_a) {
                    var noGutters = _a.noGutters;
                    return (noGutters ? 0 : grid.gutter.lg / 2);
                },
                paddingRight: function (_a) {
                    var noGutters = _a.noGutters;
                    return (noGutters ? 0 : grid.gutter.lg / 2);
                },
            },
            _a["@media (min-width: ".concat(breakpoints.xl, "px)")] = {
                flex: function (_a) {
                    var xl = _a.xl;
                    return xl && "0 0 ".concat(width(xl));
                },
                maxWidth: function (_a) {
                    var xl = _a.xl;
                    return xl && width(xl);
                },
                order: function (_a) {
                    var xlOrder = _a.xlOrder;
                    return xlOrder;
                },
                paddingLeft: function (_a) {
                    var noGutters = _a.noGutters;
                    return (noGutters ? 0 : grid.gutter.xl / 2);
                },
                paddingRight: function (_a) {
                    var noGutters = _a.noGutters;
                    return (noGutters ? 0 : grid.gutter.xl / 2);
                },
            },
            _a),
    };
});

var GridItem = function (_a) {
    var children = _a.children, _b = _a.xs, xs = _b === void 0 ? undefined : _b, _c = _a.sm, sm = _c === void 0 ? undefined : _c, _d = _a.md, md = _d === void 0 ? undefined : _d, _e = _a.lg, lg = _e === void 0 ? undefined : _e, _f = _a.xl, xl = _f === void 0 ? undefined : _f, _g = _a.xsOrder, xsOrder = _g === void 0 ? undefined : _g, _h = _a.smOrder, smOrder = _h === void 0 ? undefined : _h, _j = _a.mdOrder, mdOrder = _j === void 0 ? undefined : _j, _k = _a.lgOrder, lgOrder = _k === void 0 ? undefined : _k, _l = _a.xlOrder, xlOrder = _l === void 0 ? undefined : _l, _m = _a.noGutters, noGutters = _m === void 0 ? undefined : _m, rest = __rest(_a, ["children", "xs", "sm", "md", "lg", "xl", "xsOrder", "smOrder", "mdOrder", "lgOrder", "xlOrder", "noGutters"]);
    var noGuttersFromContext = useContext(GridContext).noGutters;
    var wrenchGridItem = useStyles({
        xs: xs,
        sm: sm,
        md: md,
        lg: lg,
        xl: xl,
        noGutters: noGutters !== null && noGutters !== void 0 ? noGutters : noGuttersFromContext,
        xsOrder: xsOrder,
        smOrder: smOrder,
        mdOrder: mdOrder,
        lgOrder: lgOrder,
        xlOrder: xlOrder,
    }).wrenchGridItem;
    return (React.createElement("div", __assign({ className: wrenchGridItem }, safeRest(rest)), children));
};

var useStylesGrid = createMemoStyles(function (theme) {
    var _a, _b;
    var breakpoints = theme.breakpoints, spacing = theme.spacing;
    var grid = {
        bleed: {
            xs: spacing[4],
            sm: spacing[6],
            md: spacing[6],
            lg: spacing[6],
            xl: spacing[6],
        },
        gutter: {
            xs: spacing[4],
            sm: spacing[5],
            md: spacing[5],
            lg: spacing[5],
            xl: spacing[5],
        },
        maxWidth: __assign(__assign({}, breakpoints), { xl: 1400 }),
        columns: 12,
    };
    return {
        wrenchGridContainer: __assign(__assign(__assign({}, defaultBaseline), defaultFonts(theme)), (_a = { display: 'flex', flexDirection: 'column', width: '100%', marginRight: 'auto', marginLeft: 'auto' }, _a["@media (min-width: ".concat(breakpoints.xs, "px)")] = {
            paddingLeft: function (_a) {
                var noBleeds = _a.noBleeds;
                return !noBleeds && grid.bleed.xs;
            },
            paddingRight: function (_a) {
                var noBleeds = _a.noBleeds;
                return !noBleeds && grid.bleed.xs;
            },
        }, _a["@media (min-width: ".concat(breakpoints.sm, "px)")] = {
            maxWidth: function (_a) {
                var fluid = _a.fluid;
                return !fluid && grid.maxWidth.sm;
            },
            paddingLeft: function (_a) {
                var noBleeds = _a.noBleeds;
                return !noBleeds && grid.bleed.sm;
            },
            paddingRight: function (_a) {
                var noBleeds = _a.noBleeds;
                return !noBleeds && grid.bleed.sm;
            },
        }, _a["@media (min-width: ".concat(breakpoints.md, "px)")] = {
            maxWidth: function (_a) {
                var fluid = _a.fluid;
                return !fluid && grid.maxWidth.md;
            },
            paddingLeft: function (_a) {
                var noBleeds = _a.noBleeds;
                return !noBleeds && grid.bleed.md;
            },
            paddingRight: function (_a) {
                var noBleeds = _a.noBleeds;
                return !noBleeds && grid.bleed.md;
            },
        }, _a["@media (min-width: ".concat(breakpoints.lg, "px)")] = {
            maxWidth: function (_a) {
                var fluid = _a.fluid;
                return !fluid && grid.maxWidth.lg;
            },
            paddingLeft: function (_a) {
                var noBleeds = _a.noBleeds;
                return !noBleeds && grid.bleed.lg;
            },
            paddingRight: function (_a) {
                var noBleeds = _a.noBleeds;
                return !noBleeds && grid.bleed.lg;
            },
        }, _a["@media (min-width: ".concat(breakpoints.xl, "px)")] = {
            maxWidth: function (_a) {
                var fluid = _a.fluid;
                return !fluid && grid.maxWidth.xl;
            },
            paddingLeft: function (_a) {
                var noBleeds = _a.noBleeds;
                return !noBleeds && grid.bleed.xl;
            },
            paddingRight: function (_a) {
                var noBleeds = _a.noBleeds;
                return !noBleeds && grid.bleed.xl;
            },
        }, _a)),
        wrenchGrid: (_b = {
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap'
            },
            _b["@media (min-width: ".concat(breakpoints.xs, "px)")] = {
                marginLeft: function (_a) {
                    var noGutters = _a.noGutters;
                    return noGutters ? 0 : grid.gutter.xs / -2;
                },
                marginRight: function (_a) {
                    var noGutters = _a.noGutters;
                    return noGutters ? 0 : grid.gutter.xs / -2;
                },
            },
            _b["@media (min-width: ".concat(breakpoints.sm, "px)")] = {
                marginLeft: function (_a) {
                    var noGutters = _a.noGutters;
                    return noGutters ? 0 : grid.gutter.sm / -2;
                },
                marginRight: function (_a) {
                    var noGutters = _a.noGutters;
                    return noGutters ? 0 : grid.gutter.sm / -2;
                },
            },
            _b["@media (min-width: ".concat(breakpoints.md, "px)")] = {
                marginLeft: function (_a) {
                    var noGutters = _a.noGutters;
                    return noGutters ? 0 : grid.gutter.md / -2;
                },
                marginRight: function (_a) {
                    var noGutters = _a.noGutters;
                    return noGutters ? 0 : grid.gutter.md / -2;
                },
            },
            _b["@media (min-width: ".concat(breakpoints.lg, "px)")] = {
                marginleft: function (_a) {
                    var noGutters = _a.noGutters;
                    return noGutters ? 0 : grid.gutter.lg / -2;
                },
                marginRight: function (_a) {
                    var noGutters = _a.noGutters;
                    return noGutters ? 0 : grid.gutter.lg / -2;
                },
            },
            _b["@media (min-width: ".concat(breakpoints.xl, "px)")] = {
                marginLeft: function (_a) {
                    var noGutters = _a.noGutters;
                    return noGutters ? 0 : grid.gutter.lg / -2;
                },
                marginRight: function (_a) {
                    var noGutters = _a.noGutters;
                    return noGutters ? 0 : grid.gutter.lg / -2;
                },
            },
            _b),
    };
});

var Grid = function (_a) {
    var children = _a.children, _b = _a.noGutters, noGutters = _b === void 0 ? false : _b, _c = _a.noBleeds, noBleeds = _c === void 0 ? false : _c, _d = _a.fluid, fluid = _d === void 0 ? false : _d, _e = _a.spacingClass, spacingClass = _e === void 0 ? undefined : _e, rest = __rest(_a, ["children", "noGutters", "noBleeds", "fluid", "spacingClass"]);
    var _f = useStylesGrid({
        noGutters: noGutters,
        noBleeds: noBleeds,
        fluid: fluid,
    }), wrenchGrid = _f.wrenchGrid, wrenchGridContainer = _f.wrenchGridContainer;
    var wrenchGridWrapper = clsx(wrenchGridContainer, spacingClass);
    var contextValue = useMemo(function () { return ({
        noGutters: noGutters,
    }); }, [noGutters]);
    return (React.createElement(GridContext.Provider, { value: contextValue },
        React.createElement("div", __assign({ className: wrenchGridWrapper }, safeRest(rest)),
            React.createElement("div", { className: wrenchGrid }, children))));
};
var GridWithSpacing = withNested(withSpacing(Grid), { Item: GridItem });

export { GridWithSpacing as Grid };
